import { motion } from "framer-motion";
import { Reveal, EASE } from "./Reveal";
import { GERMANY_STATES } from "@/data/germanyStates";

const CITIES = [
  { name: "Hamburg", x: 135, y: 72, anchor: "start", dx: 8, dy: -4 },
  { name: "Bremen", x: 96, y: 95, anchor: "end", dx: -8, dy: 2 },
  { name: "Berlin", x: 247, y: 122, anchor: "start", dx: 8, dy: 2 },
  { name: "Hannover", x: 126, y: 130, anchor: "end", dx: -8, dy: 10 },
  { name: "Köln", x: 35, y: 200, anchor: "start", dx: 8, dy: -2 },
  { name: "Frankfurt", x: 92, y: 241, anchor: "start", dx: 8, dy: 2 },
  { name: "Stuttgart", x: 108, y: 307, anchor: "end", dx: -8, dy: 2 },
  { name: "München", x: 187, y: 339, anchor: "start", dx: 8, dy: 4 },
];

const ROUTES = [
  { from: [96, 95], to: [135, 72], color: "#2EF08A", delay: 0.2 },
  { from: [247, 122], to: [126, 130], color: "#1E9BFF", delay: 0.5 },
  { from: [135, 72], to: [108, 307], color: "#2EF08A", delay: 0.8 },
  { from: [35, 200], to: [187, 339], color: "#E600FF", delay: 1.1 },
];

const curvePath = ([x1, y1], [x2, y2]) => {
  const mx = (x1 + x2) / 2;
  const my = (y1 + y2) / 2;
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.hypot(dx, dy) || 1;
  const off = Math.min(60, len * 0.22);
  const cx = mx - (dy / len) * off;
  const cy = my + (dx / len) * off;
  return `M${x1},${y1} Q${cx},${cy} ${x2},${y2}`;
};

export const GermanyMap = () => {
  return (
    <svg viewBox="-3 -4 308 388" className="h-auto w-full max-w-md" role="img" aria-label="Deutschland-Karte mit allen Bundesländern und Beispiel-Routen">
      <defs>
        <radialGradient id="de-glow" cx="50%" cy="40%" r="70%">
          <stop offset="0%" stopColor="rgba(30,155,255,0.18)" />
          <stop offset="100%" stopColor="rgba(30,155,255,0)" />
        </radialGradient>
      </defs>

      <rect x="-3" y="-4" width="308" height="388" fill="url(#de-glow)" />

      <g strokeLinejoin="round">
        {GERMANY_STATES.map((s) => (
          <path
            key={s.name}
            d={s.d}
            fill="rgba(255,255,255,0.04)"
            stroke="rgba(46,240,138,0.3)"
            strokeWidth="0.7"
            className="transition-[fill] duration-300 hover:fill-[rgba(46,240,138,0.12)]"
          >
            <title>{s.name}</title>
          </path>
        ))}
      </g>

      {ROUTES.map((r, i) => (
        <motion.path
          key={i}
          d={curvePath(r.from, r.to)}
          fill="none"
          stroke={r.color}
          strokeWidth="2"
          strokeLinecap="round"
          strokeDasharray="4 4"
          initial={{ pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 0.9 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 1.1, delay: r.delay, ease: EASE }}
          style={{ filter: `drop-shadow(0 0 4px ${r.color}66)` }}
        />
      ))}

      {CITIES.map((c, i) => (
        <g key={c.name}>
          <motion.circle
            cx={c.x}
            cy={c.y}
            r="3.5"
            fill="#2EF08A"
            initial={{ scale: 0, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.4, delay: 0.2 + i * 0.08, ease: EASE }}
            style={{ transformOrigin: `${c.x}px ${c.y}px` }}
          />
          <circle cx={c.x} cy={c.y} r="3.5" fill="none" stroke="#2EF08A" strokeWidth="1" opacity="0.5">
            <animate attributeName="r" from="3.5" to="9" dur="2.4s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" from="0.5" to="0" dur="2.4s" begin={`${i * 0.3}s`} repeatCount="indefinite" />
          </circle>
          <text
            x={c.x + c.dx}
            y={c.y + c.dy}
            textAnchor={c.anchor}
            className="font-body"
            fill="rgba(255,255,255,0.6)"
            fontSize="9"
          >
            {c.name}
          </text>
        </g>
      ))}
    </svg>
  );
};

const LEGEND = [
  { label: "Bremen → Hamburg", km: "100 km", color: "#2EF08A" },
  { label: "Berlin → Hannover", km: "300 km", color: "#1E9BFF" },
  { label: "Hamburg → Stuttgart", km: "600 km", color: "#2EF08A" },
  { label: "Köln → München", km: "quer durchs Land", color: "#E600FF" },
];

export const GermanySection = () => {
  return (
    <section id="deutschlandweit" data-testid="germany-map" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div>
            <Reveal>
              <span className="font-heading text-xs font-semibold uppercase tracking-[0.24em] text-brand-blue">
                Deutschlandweit unterwegs
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
                Aufträge gibt es in <span className="text-gradient-brand">ganz Deutschland.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-6 max-w-lg font-body text-base leading-relaxed text-white/70 sm:text-lg">
                Von Hamburg bis München werden täglich Fahrzeuge bewegt. Du bist an keine Region
                gebunden und entscheidest selbst, welche Routen zu dir passen.
              </p>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-4 max-w-lg font-body text-base leading-relaxed text-white/90 sm:text-lg">
                Egal ob <span className="font-semibold text-brand-green">Kleinstadt, Dorf oder Großstadt</span> –
                du entscheidest: <span className="font-semibold text-brand-green">Kurz-, Mittel- oder Langstrecke.</span>
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                {LEGEND.map((l) => (
                  <li key={l.label} className="flex items-center gap-3 rounded-xl border border-white/10 bg-brand-surface px-4 py-3">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: l.color }} />
                    <span className="font-heading text-sm font-semibold text-white">{l.label}</span>
                    <span className="ml-auto font-body text-xs text-white/45">{l.km}</span>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-4 font-body text-xs text-white/35">
                Beispielrouten zur Veranschaulichung – keine Einkommensgarantie.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="flex justify-center">
              <GermanyMap />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
